import { services } from "@/data/services";
import { salons } from "@/data/salons";
import { withBookingUtm } from "@/lib/booking";
import type { Service, Salon } from "@/data/types";

/** Salon-specific suffixes used by per-salon service variants (e.g. barnklippning-taby). */
const SALON_SUFFIXES = ["taby", "arninge", "akersberga", "ahlens-city"] as const;

/** Strips a trailing salon suffix from a service slug ("barnklippning-taby" -> "barnklippning"). */
function baseServiceSlug(slug: string): string {
  for (const suffix of SALON_SUFFIXES) {
    if (slug.endsWith(`-${suffix}`)) return slug.slice(0, -(suffix.length + 1));
  }
  return slug;
}

/**
 * Resolves the BokaDirekt link for a given service at a given salon.
 *
 * Order of preference:
 * 1. A per-salon service variant (e.g. `barnklippning-taby`) — books the exact
 *    service at that salon with no practitioner preselected.
 * 2. The service's own booking URL when it already points at that salon.
 * 3. The salon's BokaDirekt page.
 */
export function serviceBookingUrlAt(service: Service, salonSlug: Salon["slug"]): string {
  const salon = salons.find((s) => s.slug === salonSlug);
  const base = baseServiceSlug(service.slug);

  const variant = services.find(
    (s) =>
      baseServiceSlug(s.slug) === base &&
      s.availableAt.length === 1 &&
      s.availableAt[0] === salonSlug &&
      !!s.bookingUrl,
  );
  if (variant?.bookingUrl) return withBookingUtm(variant.bookingUrl);

  const placeId = salon?.bokaDirektUrl.split("/").pop();
  if (placeId && service.bookingUrl?.includes(placeId)) {
    return withBookingUtm(service.bookingUrl);
  }

  return withBookingUtm(salon?.bokaDirektUrl ?? service.bookingUrl ?? "");
}

/**
 * Resolves the salon a page belongs to from its pathname, so shared CTAs
 * (header, sticky mobile bar) book the salon the visitor is reading about.
 * Matches salon pages (/barberare-stockholm-taby) and per-salon service pages
 * (/barnklippning-taby, /herrklippning-arninge, ...). Returns null for generic pages.
 */
export function salonForPath(pathname: string): Salon | null {
  const path = pathname.replace(/\/+$/, "").toLowerCase();
  if (!path) return null;

  const bySalonPage = salons.find(
    (s) => path === s.url || path.startsWith(`${s.url}/`),
  );
  if (bySalonPage) return bySalonPage;

  const service = services.find((s) => s.url.replace(/\/+$/, "") === path);
  if (service) {
    if (service.availableAt.length === 1) {
      return salons.find((s) => s.slug === service.availableAt[0]) ?? null;
    }
    const suffix = SALON_SUFFIXES.find((sfx) => service.slug.endsWith(`-${sfx}`));
    if (suffix) return salons.find((s) => s.slug === suffix) ?? null;
  }
  return null;
}
